import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { X, Share2, Users, Check, AlertCircle, Plus, Loader2 } from 'lucide-react';
import UserSearchAutocomplete from '../Common/UserSearchAutocomplete';
import { shareProperty } from '../../redux/slices/analysisSlice';
import { toast } from 'react-toastify';

import { API_BASE_URL as API_BASE } from '../../config/apiConfig';
import { fetchUserByEmail } from '../../utils/fetchUserByEmail';

const SharePropertyModal = ({ isOpen, onClose, propertyAddress, propertyId }) => {
    const dispatch = useDispatch();
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSharing, setIsSharing] = useState(false);
    const [sharedSuccess, setSharedSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [sharedWithNames, setSharedWithNames] = useState([]);
    const [showAddUserForm, setShowAddUserForm] = useState(false);
    const [addUserForm, setAddUserForm] = useState({
        email: '',
        full_name: ''
    });
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [addUserError, setAddUserError] = useState('');

    const handleNoUsersFound = (email) => {
        setAddUserForm({ email, full_name: '' });
        setShowAddUserForm(true);
        setAddUserError('');
    };


    const handleAddUser = async (e) => {
        e.preventDefault();
        setAddUserError('');

        if (!addUserForm.email) {
            setAddUserError('Email is required');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(addUserForm.email)) {
            setAddUserError('Please enter a valid email address');
            return;
        }

        setAddUserLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            // Step 1: Create the user
            const response = await fetch(`${API_BASE}/auth/admin/users/add`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: addUserForm.email,
                    full_name: addUserForm.full_name || undefined
                })
            });

            if (response.ok) {
                const userData = await response.json();
                // Try to get userId from response
                let newUserId = userData.id || userData.user_id || userData.data?.id;

                // If not found, fetch user by email
                if (!newUserId) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                    const foundUser = await fetchUserByEmail(addUserForm.email, token);
                    newUserId = foundUser?.id;
                }

                // Step 2: Share the property with the newly created user
                if (newUserId && propertyId) {
                    try {
                        const shareResponse = await dispatch(shareProperty({ propertyId, userIds: [newUserId] }));
                        if (shareProperty.fulfilled.match(shareResponse)) {
                            toast.success(`User ${addUserForm.email} created successfully!\nProperty shared and notification email sent.`);
                        } else {
                            toast.error('User created but failed to share property. Please try sharing manually.');
                            setAddUserError('User created but property share failed. Please share manually from the list.');
                        }
                    } catch (shareErr) {
                        console.error('Error sharing property:', shareErr);
                        toast.error('User created but failed to share property. Please try sharing manually.');
                        setAddUserError('User created but property share failed. Please share manually from the list.');
                    }
                } else {
                    toast.error('User created but unable to share property. Missing user or property ID.');
                    setAddUserError('User created but missing ID for property sharing.');
                }

                // Only clear form and close if everything was successful
                if (!addUserError && setAddUserForm) {
                    setAddUserForm({ email: '', full_name: '' });
                    setShowAddUserForm(false);
                }
            } else {
                const errorData = await response.json();
                setAddUserError(errorData.detail || 'Failed to create user');
                toast.error(errorData.detail || 'Failed to create user');
            }
        } catch (err) {
            console.error('Error adding user:', err);
            setAddUserError('An error occurred while adding user');
            toast.error('An error occurred while adding user');
        } finally {
            setAddUserLoading(false);
        }
    };

    if (!isOpen) return null;

    const handleUserSelect = (user) => {
        if (!selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
            setError(null);
        }
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
    };

    const handleShare = async () => {
        if (!propertyId) {
            setError("Property ID is missing.");
            return;
        }
        if (selectedUsers.length === 0) return;

        setIsSharing(true);
        setError(null);

        try {
            const userIds = selectedUsers.map(u => u.id);
            const userNames = selectedUsers.map(u => u.full_name || u.email);

            const resultAction = await dispatch(shareProperty({ propertyId, userIds }));

            if (shareProperty.fulfilled.match(resultAction)) {
                setSharedWithNames(userNames);
                setSharedSuccess(true);
                // Clear selection but keep success message for a bit
                setSelectedUsers([]);

                // Auto close after 4 seconds
                setTimeout(() => {
                    handleClose();
                }, 4000);
            } else {
                setError(resultAction.payload || "Failed to share property.");
            }
        } catch (err) {
            setError("An unexpected error occurred.");
        } finally {
            setIsSharing(false);
        }
    };

    const handleClose = () => {
        setSharedSuccess(false);
        setSelectedUsers([]);
        setError(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <Share2 className="w-5 h-5" />
                            Share Analysis
                        </h2>
                        <p className="text-blue-100 text-sm mt-1">
                            Share <span className="font-semibold">{propertyAddress}</span> with your team
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {sharedSuccess ? (
                        <div className="py-8 flex flex-col items-center text-center space-y-4 animate-in fade-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2 shadow-sm">
                                <Check className="w-8 h-8 text-green-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-800">Successfully Shared!</h3>
                                <p className="text-slate-500 mt-1">The analysis has been shared with:</p>
                            </div>

                            <div className="flex flex-wrap gap-2 justify-center max-w-full">
                                {sharedWithNames.map((name, index) => (
                                    <span
                                        key={index}
                                        className="px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm font-semibold"
                                    >
                                        {name}
                                    </span>
                                ))}
                            </div>

                            <button
                                onClick={handleClose}
                                className="mt-4 px-6 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700 animate-in fade-in slide-in-from-top-2">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Search Section */}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                    <Users className="w-4 h-4 text-blue-500" />
                                    Add People
                                </label>
                                <UserSearchAutocomplete
                                    onSelect={handleUserSelect}
                                    excludeIds={selectedUsers.map(u => u.id)}
                                    placeholder="Type name or email (min 2 chars)..."
                                    onNoUsersFound={handleNoUsersFound}
                                />
                            </div>

                            {/* Selected Users List */}
                            <div className="min-h-[100px] bg-slate-50 rounded-xl p-4 border border-slate-200">
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                    Selected Users ({selectedUsers.length})
                                </div>

                                {selectedUsers.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-4 text-slate-400 text-sm italic">
                                        <Users className="w-8 h-8 mb-2 opacity-20" />
                                        No users selected yet
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {selectedUsers.map(user => (
                                            <div
                                                key={user.id}
                                                className="flex items-center gap-2 bg-white pl-2 pr-1 py-1 rounded-full border border-blue-200 shadow-sm text-sm text-slate-700 animate-in zoom-in duration-200"
                                            >
                                                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-[10px] text-white font-bold">
                                                    {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <span className="font-medium max-w-[120px] truncate">
                                                    {user.full_name || user.email}
                                                </span>
                                                <button
                                                    onClick={() => handleRemoveUser(user.id)}
                                                    className="p-0.5 hover:bg-slate-100 rounded-full text-slate-400 hover:text-red-500 transition-colors"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Add User Form */}
                            {showAddUserForm && (
                                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                            <Plus className="w-4 h-4 text-indigo-600" />
                                            Add New User
                                        </h3>
                                        <button
                                            onClick={() => {
                                                setShowAddUserForm(false);
                                                setAddUserForm({ email: '', full_name: '' });
                                                setAddUserError('');
                                            }}
                                            className="text-slate-400 hover:text-slate-600"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleAddUser} className="space-y-3">
                                        {/* Email */}
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                                                Email Address
                                            </label>
                                            <input
                                                type="email"
                                                value={addUserForm.email}
                                                onChange={(e) => setAddUserForm({
                                                    ...addUserForm,
                                                    email: e.target.value
                                                })}
                                                placeholder="user@example.com"
                                                className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                                disabled={addUserLoading}
                                            />
                                        </div>

                                        {/* Full Name */}
                                        <div>
                                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                                                Full Name (Optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={addUserForm.full_name}
                                                onChange={(e) => setAddUserForm({
                                                    ...addUserForm,
                                                    full_name: e.target.value
                                                })}
                                                placeholder="John Doe"
                                                className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                                disabled={addUserLoading}
                                            />
                                        </div>

                                        {/* Error Message */}
                                        {addUserError && (
                                            <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-700 animate-in fade-in">
                                                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                                <span>{addUserError}</span>
                                            </div>
                                        )}

                                        <p className="text-xs text-slate-600 italic">
                                            A temporary password will be generated and sent to the user's email.
                                        </p>

                                        {/* Buttons */}
                                        <div className="flex gap-2 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowAddUserForm(false);
                                                    setAddUserForm({ email: '', full_name: '' });
                                                    setAddUserError('');
                                                }}
                                                disabled={addUserLoading}
                                                className="flex-1 px-3 py-1.5 border border-indigo-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-indigo-100 transition-all disabled:bg-slate-100"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={addUserLoading}
                                                className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all disabled:bg-slate-400"
                                            >
                                                {addUserLoading ? (
                                                    <>
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus className="w-3 h-3" />
                                                        Add User
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                {!sharedSuccess && (
                    <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm font-semibold transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleShare}
                            disabled={selectedUsers.length === 0 || isSharing}
                            className={`px-6 py-2 rounded-lg text-sm font-bold text-white shadow-lg transition-all flex items-center gap-2
                                ${selectedUsers.length === 0 || isSharing
                                    ? 'bg-slate-400 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-blue-500/25'
                                }`}
                        >
                            {isSharing ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Sharing...
                                </>
                            ) : (
                                <>
                                    <Share2 className="w-4 h-4" />
                                    Share Analysis
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharePropertyModal;
